import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  // DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {

  Users,
  BarChart3,
  Shield,
  RefreshCw,
  UserPlus,
  AlertCircle,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { IUser } from "@/services/mocks/userData";
import {
  userService,
  // IUsersResponse,
  // ICreateUserData,
} from "@/services";
import UserManagementTable from "@/components/business/admin/UserManagementTable";
import UserForm, { IUserFormData } from "@/components/business/admin/UserForm";
import { useToast } from "@/hooks/useToast";

/**
 * Página de administración del sistema
 *
 * Esta página está reservada para usuarios con rol Admin
 * y contiene las herramientas de administración del sistema,
 * empezando por la gestión de usuarios.
 * Optimizada para dispositivos móviles con layout responsive.
 */
const AdminPage: React.FC = () => {
  const { toast } = useToast();

  // Estados para la gestión de usuarios
  const [users, setUsers] = useState<IUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<{
    total: number;
    active: number;
    inactive: number;
    byRole: Record<string, number>;
  } | null>(null);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);

  // Estados para el modal de creación
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Estados para el modal de edición
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<IUser | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Estados para el diálogo de eliminación
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<IUser | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  /**
   * Carga los usuarios del sistema
   */
  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [usersResponse, statsData, roles] = await Promise.all([
        userService.getAllUsers(),
        userService.getUserStats(),
        userService.getRoles(),
      ]);

      setUsers(usersResponse.users);
      setStats(statsData);
      setAvailableRoles(roles);

      toast({
        title: "Usuarios cargados",
        description: `Se cargaron ${usersResponse.total} usuarios exitosamente`,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Error al cargar usuarios";
      setError(errorMessage);

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Maneja la acción de editar usuario
   */
  const handleEditUser = (user: IUser) => {
    setEditingUser(user);
    setIsEditModalOpen(true);
  };

  /**
   * Maneja la acción de agregar nuevo usuario
   */
  const handleAddUser = () => {
    setIsCreateModalOpen(true);
  };

  /**
   * Maneja selección/toggle de filtro por rol
   */
  const handleSelectRole = (role: string) => {
    setSelectedRole((prev) => (prev === role ? null : role));
  };

  /**
   * Maneja la creación de un nuevo usuario
   */
  const handleCreateUser = async (formData: IUserFormData) => {
    setIsCreating(true);

    try {
      const newUser = await userService.createUser(formData);

      // Recargar la lista completa de usuarios para evitar duplicados
      const usersResponse = await userService.getAllUsers();
      setUsers(usersResponse.users);

      // Actualizar estadísticas
      const newStats = await userService.getUserStats();
      setStats(newStats);

      // Cerrar modal y mostrar notificación
      setIsCreateModalOpen(false);
      toast({
        title: "Usuario Creado",
        description: `${newUser.nombre} ${newUser.apellido} ha sido creado exitosamente`,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Error al crear usuario";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Maneja el cierre del modal de creación
   */
  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  /**
   * Maneja el cierre del modal de edición
   */
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingUser(null);
  };

  /**
   * Maneja la actualización de un usuario
   */
  const handleUpdateUser = async (formData: IUserFormData) => {
    if (!editingUser) return;

    setIsUpdating(true);

    try {
      const updatedUser = await userService.updateUser(
        editingUser.id,
        formData
      );

      // Recargar la lista completa de usuarios
      const usersResponse = await userService.getAllUsers();
      setUsers(usersResponse.users);

      // Actualizar estadísticas
      const newStats = await userService.getUserStats();
      setStats(newStats);

      // Cerrar modal y mostrar notificación
      setIsEditModalOpen(false);
      setEditingUser(null);
      toast({
        title: "Usuario Actualizado",
        description: `${updatedUser.nombre} ${updatedUser.apellido} ha sido actualizado exitosamente`,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Error al actualizar usuario";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * Maneja la acción de eliminar usuario
   */
  const handleDeleteUser = (user: IUser) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  /**
   * Maneja la confirmación de eliminación
   */
  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);

    try {
      await userService.deleteUser(userToDelete.id);

      // Recargar la lista completa de usuarios
      const usersResponse = await userService.getAllUsers();
      setUsers(usersResponse.users);

      // Actualizar estadísticas
      const newStats = await userService.getUserStats();
      setStats(newStats);

      // Cerrar diálogo y mostrar notificación
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
      toast({
        title: "Usuario Eliminado",
        description: `${userToDelete.nombre} ${userToDelete.apellido} ha sido eliminado exitosamente`,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Error al eliminar usuario";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Maneja la cancelación de eliminación
   */
  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  // Cargar usuarios al montar el componente
  useEffect(() => {
    loadUsers();
  }, []);

  // Búsqueda por nombre/apellido/email con debounce
  useEffect(() => {
    const handler = setTimeout(async () => {
      if (!searchTerm.trim()) {
        // si no hay término, recargar todos
        await loadUsers();
        return;
      }
      setIsSearching(true);
      try {
        const resp = await userService.searchUsers(searchTerm.trim());
        setUsers(resp.users);
      } catch (e) {
        // mantener lista anterior en caso de error
      } finally {
        setIsSearching(false);
      }
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    console.log("users", users);
  }, [users]);

  // Lista de usuarios filtrada por rol si corresponde
  const filteredUsers = selectedRole
    ? users.filter((u) => Array.isArray(u.roles) && u.roles.includes(selectedRole))
    : users;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Gestión de Usuarios
          </h1>
          <p className="text-muted-foreground mt-2">
            Administra usuarios, roles y permisos del sistema
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
          <div className="w-full sm:w-64">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre, apellido o email"
              className="h-10"
            />
          </div>
          <Button
            variant="outline"
            onClick={loadUsers}
            disabled={isLoading || isSearching}
            className="flex items-center space-x-2 h-10"
          >
            <RefreshCw
              className={`h-4 w-4 ${(isLoading || isSearching) ? "animate-spin" : ""}`}
            />
            <span>Actualizar</span>
          </Button>

          <Button
            onClick={handleAddUser}
            className="flex items-center space-x-2 h-10"
          >
            <UserPlus className="h-4 w-4" />
            <span>Agregar Usuario</span>
          </Button>
        </div>
      </div>

      {/* Estadísticas - Vista Desktop */}
      {stats && (
        <div className="hidden lg:grid grid-cols-4 gap-4">
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-foreground">
                Total Usuarios
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">
                {stats.total}
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-foreground">
                Usuarios Activos
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">
                {stats.active}
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-foreground">
                Usuarios Inactivos
              </CardTitle>
              <XCircle className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">
                {stats.inactive}
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-foreground">
                Roles Únicos
              </CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">
                {Object.keys(stats.byRole).length}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Estadísticas - Vista Mobile/Tablet con Scroll Horizontal */}
      {stats && (
        <div className="lg:hidden">
          <div className="flex space-x-4 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
            <Card className="min-w-[200px] flex-shrink-0 hover:shadow-md transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-foreground">
                  Total Usuarios
                </CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">
                  {stats.total}
                </div>
              </CardContent>
            </Card>

            <Card className="min-w-[200px] flex-shrink-0 hover:shadow-md transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-foreground">
                  Usuarios Activos
                </CardTitle>
                <CheckCircle className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-primary">
                  {stats.active}
                </div>
              </CardContent>
            </Card>

            <Card className="min-w-[200px] flex-shrink-0 hover:shadow-md transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-foreground">
                  Usuarios Inactivos
                </CardTitle>
                <XCircle className="h-4 w-4 text-destructive" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-destructive">
                  {stats.inactive}
                </div>
              </CardContent>
            </Card>

            <Card className="min-w-[200px] flex-shrink-0 hover:shadow-md transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-foreground">
                  Roles Únicos
                </CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">
                  {Object.keys(stats.byRole).length}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Distribución de Roles */}
      {stats && (
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-foreground">
              <Shield className="h-5 w-5" />
              Distribución por Roles
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              Cantidad de usuarios por cada rol en el sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={selectedRole === null ? "default" : "outline"}
                onClick={() => setSelectedRole(null)}
                className="h-8"
              >
                Todos ({stats.total})
              </Button>
              {Object.entries(stats.byRole).map(([role, count]) => {
                const isActive = selectedRole === role;
                return (
                  <Button
                    key={role}
                    size="sm"
                    variant={isActive ? "default" : "outline"}
                    onClick={() => handleSelectRole(role)}
                    aria-pressed={isActive}
                    className="h-8 gap-2"
                  >
                    <span>{role}</span>
                    <Badge
                      variant={isActive ? "secondary" : "outline"}
                      className="text-xs"
                    >
                      {count}
                    </Badge>
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mensaje de Error */}
      {error && (
        <Alert variant="destructive" className="border-destructive/50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-destructive-foreground">
            {error}
            <Button
              variant="outline"
              size="sm"
              onClick={loadUsers}
              className="ml-2 h-8"
            >
              Reintentar
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Tabla de Usuarios */}
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Users className="h-5 w-5" />
            Lista de Usuarios
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {isSearching
              ? "Buscando..."
              : filteredUsers.length > 0
              ? `${filteredUsers.length} usuario${filteredUsers.length === 1 ? "" : "s"} ${selectedRole ? `con rol "${selectedRole}"` : "registrados en el sistema"}`
              : selectedRole
              ? `No hay usuarios con rol "${selectedRole}"`
              : "No hay usuarios para mostrar"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <UserManagementTable
            users={filteredUsers}
            onEditUser={handleEditUser}
            onDeleteUser={handleDeleteUser}
            isLoading={isLoading}
          />
        </CardContent>
      </Card>

      {/* Información de desarrollo */}
      {/* <div className="p-4 bg-muted/50 rounded-lg border border-border">
        <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-primary" />
          🚧 Funcionalidades en Desarrollo
        </h3>
        <p className="text-muted-foreground text-sm">
          Las siguientes funcionalidades están siendo implementadas:
        </p>
        <ul className="text-muted-foreground text-sm mt-2 space-y-1">
          <li>• Creación de nuevos usuarios</li>
          <li>• Edición de información de usuarios</li>
          <li>• Asignación y modificación de roles</li>
          <li>• Activación/desactivación de usuarios</li>
          <li>• Búsqueda y filtrado avanzado</li>
        </ul>
      </div> */}

      {/* Modal de Creación de Usuario */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              Crear Nuevo Usuario
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Completa la información del nuevo usuario del sistema. Todos los
              campos son obligatorios.
            </DialogDescription>
          </DialogHeader>

          <UserForm
            onSubmit={handleCreateUser}
            isLoading={isCreating}
            mode="create"
            availableRoles={availableRoles}
          />

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseCreateModal}
              disabled={isCreating}
              className="h-10"
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Edición de Usuario */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              Editar Usuario
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Modifica la información del usuario {editingUser?.nombre}{" "}
              {editingUser?.apellido}. Algunos campos no son editables por
              seguridad.
            </DialogDescription>
          </DialogHeader>

          {editingUser && (
            <UserForm
              onSubmit={handleUpdateUser}
              isLoading={isUpdating}
              mode="edit"
              isEditing={true}
              availableRoles={availableRoles}
              initialData={{
                nombres: editingUser.nombre,
                apellidos: editingUser.apellido,
                rut: editingUser.rut,
                email: editingUser.email,
                roles: editingUser.roles,
              }}
            />
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseEditModal}
              disabled={isUpdating}
              className="h-10"
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog para confirmación de eliminación */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              ¿Estás absolutamente seguro?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Esta acción no se puede deshacer. Esto eliminará permanentemente
              al usuario{" "}
              <strong className="text-foreground">
                {userToDelete?.nombre} {userToDelete?.apellido}
              </strong>{" "}
              y todos sus datos asociados del sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleCancelDelete}
              disabled={isDeleting}
              className="h-10"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="h-10"
            >
              {isDeleting ? "Eliminando..." : "Sí, eliminar usuario"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPage;
